import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEW_HERO_IMG = "/media/hero-2026/hero-1920.jpg";
const NEW_HERO_IMG_WEBP_SRCSET = [
  "/media/hero-2026/hero-640.webp 640w",
  "/media/hero-2026/hero-960.webp 960w",
  "/media/hero-2026/hero-1280.webp 1280w",
  "/media/hero-2026/hero-1600.webp 1600w",
  "/media/hero-2026/hero-1920.webp 1920w",
].join(", ");
const NEW_HERO_IMG_JPG_SRCSET = [
  "/media/hero-2026/hero-640.jpg 640w",
  "/media/hero-2026/hero-1920.jpg 1920w",
].join(", ");

const DEFAULT_CONTENT = {
  badge: "Since 2014 \u2022 ISO 9001:2015 Certified",
  headline: "Delivering Scalable Modular Office Furniture Solutions For Growing Corporates",
  subheadline: "From concept to installation \u2014 complete workspace furniture execution under one roof. Modular office furniture Manufacturer in Mumbai since 2014.",
  ctaText: "View Our Corporate Projects",
  ctaLink: "/case-studies",
  ctaSecondary: "Request Project Consultation",
  backgroundImage: NEW_HERO_IMG,
};

export default function Hero() {
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/hero`);
        if (response.data && Object.keys(response.data).length > 0) {
          setContent((prev) => ({ ...prev, ...response.data }));
        }
      } catch (err) {
        console.error("Failed to fetch hero content, using defaults:", err);
      }
    };
    fetchContent();
  }, []);

  // Split headline for styling
  const headlineParts = content.headline?.split("For Growing Corporates") || [content.headline];
  const hasOrangePart = content.headline?.includes("For Growing Corporates");

  return (
    <section className="hero-section relative" data-testid="hero-section">
      {/* Background Image — responsive WebP + JPG fallback, eager-loaded for LCP */}
      <div className="hero-bg">
        <picture style={{ display: "contents" }}>
          <source
            type="image/webp"
            srcSet={NEW_HERO_IMG_WEBP_SRCSET}
            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 100vw, 1920px"
          />
          <img
            src={NEW_HERO_IMG}
            srcSet={NEW_HERO_IMG_JPG_SRCSET}
            sizes="(max-width: 768px) 100vw, 1920px"
            alt="Open-plan modular office with workstations, acoustic phone booth and city skyline view — manufactured and installed by Ezeefit Office Systems, Mumbai (since 2014)"
            width="1920"
            height="981"
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        </picture>
      </div>

      {/* Content — left-aligned hero text; extra right padding on mobile so it doesn't sit under the right-side strips */}
      <div className="relative z-10 pl-6 pr-12 md:pl-12 lg:pl-16 xl:pl-20 2xl:pl-28 md:pr-12 lg:pr-24 py-32 pt-40">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block font-mono text-sm text-[#F97316] tracking-wider uppercase mb-6 bg-white/10 backdrop-blur-sm px-4 py-2">
              {content.badge}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-tight mb-6"
          >
            {hasOrangePart ? (
              <>
                {headlineParts[0]}
                <span className="block text-[#F97316]">For Growing Corporates</span>
              </>
            ) : (
              content.headline
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-[#CBD5E1] mb-2 max-w-2xl"
          >
            {content.subheadline}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="text-sm text-[#94A3B8] mb-10 font-medium tracking-wide"
          >
            By Ezeefit Office Systems — Since 2014
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button
              asChild
              className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
            >
              <Link to="/case-studies">
                {content.ctaText}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            
            <Button
              asChild
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white hover:text-[#0F172A] rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
            >
              <Link to="/contact">
                {content.ctaSecondary}
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
